/**
 * How a single variant ships, derived from its commercetools dimensions.
 *
 * The UPS limits are the ones checkout routes on (ShippingService sends anything over them to R&L
 * freight), and the same limits label each variant for the PDP schema and the products feed, so
 * Google is told the same parcel/freight split the cart charges.
 */
export const PARCEL_LIMITS = {
  maxWeight: 150, // lb, per unit
  maxLength: 108, // in
  maxWidth: 15, // in
  maxHeight: 15, // in
} as const;

/** Values double as the Merchant Center `shipping_label` / `transit_time_label`. */
export type ShippingClass = 'parcel' | 'freight' | 'quoted';

/* Carrier transit in business days, published as schema transitTime. Placeholders until ops
   confirms them; handling time (the variant's lead time) is the authoritative part. */
export const TRANSIT_DAYS: Record<Exclude<ShippingClass, 'quoted'>, { min: number; max: number }> = {
  parcel: { min: 1, max: 5 },
  freight: { min: 3, max: 7 },
};

export interface ShippingDimensions {
  weight?: number;
  length?: number;
  width?: number;
  height?: number;
}

const isMeasured = (value?: number) => typeof value === 'number' && value > 0;

/** A missing measurement counts as within the limit; callers decide what an unmeasured item means. */
export const exceedsParcelLimits = ({ weight = 0, length = 0, width = 0, height = 0 }: ShippingDimensions) =>
  weight > PARCEL_LIMITS.maxWeight ||
  length > PARCEL_LIMITS.maxLength ||
  width > PARCEL_LIMITS.maxWidth ||
  height > PARCEL_LIMITS.maxHeight;

/**
 * `quoted` when any measurement is missing (the made-to-order "Custom …" SKUs): checkout cannot rate
 * those either and falls back to Viewrail arranging shipping.
 */
export const getShippingClass = ({ weight, length, width, height }: ShippingDimensions): ShippingClass => {
  if (![weight, length, width, height].every(isMeasured)) return 'quoted';

  return exceedsParcelLimits({ weight, length, width, height }) ? 'freight' : 'parcel';
};
